import { defineStore } from 'pinia';
import { computed } from 'vue';
import { Lang } from '~/core';
import { normalizeNamespaceAndPath } from '~/helpers';
import { useModelsStore, useUserStore } from '~/stores';

export async function permissionsStateLoaded(permission: string | string[]): Promise<boolean> {
    if (Array.isArray(permission)) {
        const results = await Promise.all(permission.map((permission) => permissionsStateLoaded(permission)));
        return results.every((elem) => elem);
    }

    const modelsStore = useModelsStore();
    const userStore = useUserStore();

    const normalized = normalizeNamespaceAndPath(permission);
    const namespace = normalized.split(':').shift();

    switch (namespace) {
        case 'server':
            return !!modelsStore.server;
        case 'admin':
            return !!userStore.data;
    }

    return false;
}

// NOTE: The initial page load will not have the required data for this due to how we prefer skeleton loading
// for page contents. Instead, using `computed` should be enough to make the perm state refresh after it is loaded.
export async function hasPermissionsAsync(permission: string | string[], matchAny = false): Promise<boolean> {
    if (Array.isArray(permission)) {
        const results = await Promise.all(permission.map((permission) => hasPermissionsAsync(permission)));
        return results[matchAny ? 'some' : 'every']((elem) => elem);
    }

    const modelsStore = useModelsStore();
    const userStore = useUserStore();

    const normalized = normalizeNamespaceAndPath(permission);
    const namespace = normalized.split(':').shift();

    switch (namespace) {
        case 'server':
            return modelsStore.server?.relationships?.permissions.includes(normalized) || false;
        case 'admin':
            return userStore.data?.relationships?.permissions.includes(normalized) || false;
        default: {
            // FIXME this is fucking stupid
            return (
                modelsStore.server?.relationships?.permissions.includes(normalized) ||
                userStore.data?.relationships?.permissions.includes(normalized) ||
                false
            );
        }
    }
}

export const usePermissionsStore = defineStore('permissions', () => {
    const modelsStore = useModelsStore();
    const userStore = useUserStore();

    const permissionMap = computed(() => {
        const result = { server: new Set<string>(), user: new Set<string>() };

        const serverPerms = modelsStore.server?.relationships?.permissions ?? [];
        const userPerms = userStore.data?.relationships?.permissions ?? [];

        for (const p of serverPerms) result.server.add(p);
        for (const p of userPerms) result.user.add(p);

        return result;
    });

    function has(permission: string | string[], matchAny = false): boolean {
        if (Array.isArray(permission)) {
            const checks = permission.map((p) => has(p));
            return matchAny ? checks.some(Boolean) : checks.every(Boolean);
        }

        const normalized = normalizeNamespaceAndPath(permission);
        const namespace = normalized.split(':').shift();

        switch (namespace) {
            case 'server':
                return permissionMap.value.server.has(normalized);
            case 'admin':
                return permissionMap.value.user.has(normalized);
            default: {
                // FIXME this is fucking stupid
                return permissionMap.value.server.has(normalized) || permissionMap.value.user.has(normalized);
            }
        }
    }

    return { has };
});

export function translateRequiresPermissions(permissions: string | string[]): string {
    const normalized = (Array.isArray(permissions) ? permissions : [permissions]).map(normalizeNamespaceAndPath);

    // Typings seem to be broken for vue-i18n - it thinks Lang.global is the legacy mode interface instead of the composer one :/
    return (Lang.global.t as any)(
        'generic.requires_permission',
        {
            permission: normalized
                .map((permission) => {
                    const [namespace, path] = permission.split(':');

                    const tmp = path.split('.');
                    tmp.pop();
                    const category = tmp.join('.');

                    return `${Lang.global.t(`permissions.${namespace}.${category}.title`)} - ${Lang.global.t(`permissions.${namespace}.${path}.title`)}`;
                })
                .map((str) => `\`${str}\``)
                .join(', '),
        },
        Array.isArray(permissions) ? 2 : 1,
    );
}
